/**
 * FAQ client-side routing — the hub and its topic panels on one page.
 *
 * The FAQ is a single page. Every topic's questions are rendered server-side and
 * hidden; choosing a topic swaps the visible region rather than loading a new
 * document. There is no fetch and no metaobject URL involved.
 *
 *   <faq-topics>  the controller. Owns the hub view and one panel per topic,
 *                 the `#handle` in the address bar, and back/forward.
 *
 *   <faq-hero>    the hero band. Hides itself while a topic is open, because the
 *                 topic frames have no hero — the panel carries its own
 *                 breadcrumb, title and search instead.
 *
 * Both elements live in this one file on purpose: they are two halves of one
 * routing concern and share `activeHandle` through the module scope. A document
 * event alone would be a race — a deep-linked load dispatches from
 * <faq-topics>'s connectedCallback, which can run before <faq-hero> has
 * upgraded and started listening. Reading shared state on connect cannot miss.
 */

const FAQ_TOPIC_EVENT = 'faq:topic-change';

/** Handle of the open topic; empty string means the hub is showing. */
let activeHandle = '';

/** Every connected <faq-hero>, so a late upgrade still gets the current state. */
const heroes = new Set();

const applyToHeroes = () => {
  for (const hero of heroes) hero.syncToRoute();
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!customElements.get('faq-hero')) {
  customElements.define(
    'faq-hero',
    class FaqHero extends HTMLElement {
      connectedCallback() {
        heroes.add(this);
        this.syncToRoute();
      }

      disconnectedCallback() {
        heroes.delete(this);
      }

      syncToRoute() {
        this.hidden = activeHandle !== '';
      }
    },
  );
}

if (!customElements.get('faq-topics')) {
  customElements.define(
    'faq-topics',
    class FaqTopics extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
      }

      connectedCallback() {
        this.hubView = this.querySelector('[data-faq-view="hub"]');
        this.topicViews = Array.from(this.querySelectorAll('[data-faq-view="topic"]'));

        this.addEventListener('click', this.handleClick);
        window.addEventListener('popstate', this.handlePopState);

        // A deep link (…/pages/faq#orders) opens that topic on first paint. No
        // scroll or focus move: neither is a response to anything the visitor
        // just did on this page.
        this.show(this.handleFromLocation(), { focus: false, scroll: false });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('popstate', this.handlePopState);
      }

      /** The hash, but only if it names a topic actually on the page. */
      handleFromLocation() {
        let raw = window.location.hash.slice(1);
        if (!raw) return '';

        try {
          raw = decodeURIComponent(raw);
        } catch {
          // A malformed escape sequence is not a topic handle.
          return '';
        }

        return this.topicViews.some((view) => view.dataset.faqTopicHandle === raw) ? raw : '';
      }

      handleClick(event) {
        const link = event.target.closest('[data-faq-topic-link]');
        if (link) {
          event.preventDefault();
          this.navigate(link.dataset.faqTopicLink);
          return;
        }

        const back = event.target.closest('[data-faq-back]');
        if (!back) return;

        event.preventDefault();
        this.navigate('');
      }

      handlePopState() {
        this.show(this.handleFromLocation(), { focus: true, scroll: true });
      }

      navigate(handle) {
        // Rebuilt from the live URL so the ?view= parameter the page may be
        // previewed under survives the hash change.
        const url = new URL(window.location.href);
        url.hash = handle ? `#${encodeURIComponent(handle)}` : '';
        window.history.pushState({ faqTopic: handle }, '', url);

        this.show(handle, { focus: true, scroll: true });
      }

      show(handle, { focus, scroll }) {
        const active = this.topicViews.find((view) => view.dataset.faqTopicHandle === handle) ?? null;

        activeHandle = active ? handle : '';

        if (this.hubView) this.hubView.hidden = active !== null;
        for (const view of this.topicViews) view.hidden = view !== active;

        applyToHeroes();

        // Lets the search inputs clear themselves, so a query typed in one view
        // does not silently filter the next one.
        document.dispatchEvent(new CustomEvent(FAQ_TOPIC_EVENT, { detail: { handle: activeHandle } }));

        if (scroll) {
          this.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        }

        if (focus) {
          const region = active ?? this.hubView;
          region?.querySelector('[data-faq-focus]')?.focus();
        }
      }
    },
  );
}
